import { useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { Typography, Tooltip, Checkbox, FormControlLabel, Box, MenuItem, IconButton } from '@mui/material';
import { useDispatch } from '../../../../redux/store';
import { getInvoiceFiles, renderInvoiceFile } from '../../../../redux/slices/invoicing';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { ConfirmDialogModal } from '../../../../reusable-components/confirm-dialog';
import { AwaitButton } from '../../../../reusable-components/await-button';
import { ScopeGuard } from '../../../../reusable-components/scopes';
import { setInvoiceFileAsMain, deleteInvoiceFile, downloadInvoiceFile } from '../../../../api/invoicing';
import { downloadInvoiceFileInNewTab, openInvoiceFileInNewTab } from '../../../../utils/helpers/invoiceFileActions';
import InvoiceFileAttachmentRow from '../../../../reusable-components/invoice-file-attachment/InvoiceFileAttachmentRow';
import Iconify from '../../../../reusable-components/iconify';
import { useIsInvoiceEditable } from './reusable/hooks/useInvoiceLocks';

InvoiceAttachment.propTypes = {
  file: PropTypes.object,
  hasEditAccess: PropTypes.bool,
  setSelectedFileId: PropTypes.func,
};

export function InvoiceAttachment({ file, hasEditAccess, setSelectedFileId }) {
  const { id } = useParams();
  const isChatFile = file?.chatId != null;
  const { isEditable } = useIsInvoiceEditable(true);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const handleClickAttachment = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await openInvoiceFileInNewTab({
      id: file.id,
      mainFile: false,
      fileName: file.fileName,
      fileExtension: file.fileExtension,
      downloadInvoiceFileApi: downloadInvoiceFile,
    });
  };

  const handleDelete = async (fileId) => {
    const response = await deleteInvoiceFile(fileId);

    if (response.status === 200 && response.data === true) {
      enqueueSnackbar('File deleted successfully', { variant: 'success' });
      setSelectedFileId(0);
      await dispatch(renderInvoiceFile(id, true));
      await dispatch(getInvoiceFiles(id));
    } else {
      enqueueSnackbar('This file cannot be deleted from the invoice', { variant: 'error' });
    }
    setIsConfirmDialogOpen(false); // Always close dialog after delete
  };

  const handleSetMain = async (fileId) => {
    const response = await setInvoiceFileAsMain(fileId);
    setSelectedFileId(fileId);
    if (response.status === 200 && response.data) enqueueSnackbar('Main file set successfully', { variant: 'success' });
    else enqueueSnackbar('Main file set error', { variant: 'error' });
    await dispatch(getInvoiceFiles(id));
  };

  const handleDownload = (closeMenu) => {
    if (!file?.id) return;
    downloadInvoiceFileInNewTab({ id: file.id, mainFile: false });
    closeMenu?.();
  };

  return (
    <>
      <InvoiceFileAttachmentRow
        file={file}
        fileName={file.fileExtension !== 'pdf' ? `${file.fileExtension} file` : file.fileName}
        onOpen={handleClickAttachment}
        onDownload={handleDownload}
        menuTriggerRenderer={(onClick) => (
          <ScopeGuard scopes={['Invoice-Edit']} allowAdmin>
            <IconButton onClick={onClick}>
              <Iconify icon="mdi:dots-vertical" />
            </IconButton>
          </ScopeGuard>
        )}
        additionalMenuItems={(closeMenu) => (
          <>
            {hasEditAccess && (
              <Tooltip
                title={isChatFile ? "Chat files can't be removed" : ''}
                disableHoverListener={!isChatFile}
                placement="bottom"
              >
                <span>
                  <MenuItem
                    disabled={isChatFile}
                    onClick={(e) => {
                      if (isChatFile) return;
                      e.stopPropagation();
                      closeMenu();
                      setIsConfirmDialogOpen(true);
                    }}
                  >
                    <Iconify icon="mdi:trash-outline" sx={{ mr: 0.5 }} />
                    <Typography sx={{ display: 'inline-block' }}>
                      Delete
                    </Typography>
                  </MenuItem>
                </span>
              </Tooltip>
            )}
            {file.fileExtension?.includes('pdf') && (
              <MenuItem>
                <Box>
                  <AwaitButton color="inherit">
                    <Tooltip title="This file gets submitted to the booking system">
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="default"
                            sx={{ cursor: 'pointer', display: 'flex', p: '0 3px' }}
                            disabled={file.invoiceAttachmentCategoryId === 1 || !isEditable}
                            checked={file.invoiceAttachmentCategoryId === 1}
                            onChange={() => {
                              handleSetMain(file.id);
                              closeMenu();
                            }}
                          />
                        }
                        label="Main"
                      />
                    </Tooltip>
                  </AwaitButton>
                </Box>
              </MenuItem>
            )}
          </>
        )}
      />

      {!isChatFile && (
        <ConfirmDialogModal
          icon="delete-outline"
          color="error"
          content="Are you sure you want to delete this invoice file?"
          actionButton="Delete"
          action={() => handleDelete(file.id)}
          title="Delete"
          setOpenConfirm={setIsConfirmDialogOpen}
          openConfirm={isConfirmDialogOpen}
        />
      )}
    </>
  );
}
